import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AiCreditAction, AiCredits, AiJobState } from '../models';
import { AiPreviewJobService } from './ai-preview-job.service';
import { DnDesignerService } from './dn-designer.service';

/**
 * The account's AI credits for the month, shared by everything that shows them or asks for a design. The server is the
 * one that charges (a design takes its credits when it is made, a failed one takes none), so this only reads: it loads
 * once, and again whenever a design that was being made finishes or fails, so the number on screen follows the charge.
 */
@Injectable({ providedIn: 'root' })
export class AiCreditsService {
    /** Null until the first load, and when it fails: the meter then says nothing rather than something wrong. */
    readonly credits$ = new BehaviorSubject<AiCredits | null>(null);

    private lastJobKind: AiJobState['kind'] = 'idle';
    private jobSubscription?: Subscription;
    private loading = false;

    constructor(private designer: DnDesignerService, private jobs: AiPreviewJobService) {}

    get credits(): AiCredits | null {
        return this.credits$.value;
    }

    /** Starts following the design jobs (a no-op after the first call) and loads the balance. */
    start(): void {
        if (!this.jobSubscription) {
            this.lastJobKind = this.jobs.state$.value.kind;
            this.jobSubscription = this.jobs.state$.subscribe((state) => {
                if (this.lastJobKind === 'generating' && state.kind !== 'generating') { this.refresh(); }
                this.lastJobKind = state.kind;
            });
        }
        this.refresh();
    }

    refresh(): void {
        if (this.loading) { return; }
        this.loading = true;
        this.designer.getAiCredits().subscribe({
            next: (credits) => { this.credits$.next(credits); this.loading = false; },
            error: () => { this.loading = false; /* keep showing the last balance, if there was one */ },
        });
    }

    /** Credits an action takes, or 1 when the costs have not loaded. */
    costOf(action: AiCreditAction): number {
        return this.credits?.costs?.[action] ?? 1;
    }

    /** True unless the balance is known and does not cover the action. */
    canAfford(action: AiCreditAction): boolean {
        const credits = this.credits;
        return !credits || credits.remaining >= this.costOf(action);
    }
}
